/**
 * ── THE CUSTOMER'S PURCHASE ORDER, ON THE WAY TO AN INVOICE ───────────────────────────────────
 *
 * Requester, 2026-09-21: converting a quote should "pop out window to enter the PO number", that
 * number goes "in the memo of the invoice", and the PO document is uploaded "under the customer's
 * document center".
 *
 * ONE COMPONENT, TWO DIALOGS. Converting a quote and raising an invoice directly are the two
 * routes to the same document, and a rep should not meet a different PO form depending on which
 * one they took. Sharing it is also what keeps the upload's ONE subtle rule in one place rather
 * than copied into two.
 *
 * ── WHY THE UPLOAD IS ALWAYS OFFERED, THOUGH IT CANNOT ALWAYS SUCCEED ─────────────────────────
 *
 * The Document Center files by ACCOUNT, and `intl_requests.account_id` is frequently NULL. The
 * first version of this read that column and hid the upload when it was empty — which was wrong,
 * and wrong in the way that is hardest to notice: NULL there means the ROW does not name an
 * account, NOT that the customer has none. Most of these requests simply never recorded one, so
 * the control disappeared for perfectly ordinary customers and the feature looked broken.
 *
 * The question the column cannot answer is answered by the STRIPE CUSTOMER ID, which every
 * request carries and which the Document Center's own upload endpoint already resolves. So the
 * control is always drawn and `stripeId` is sent alongside whatever the row knows; the server
 * decides. A genuine prospect — a Stripe customer with no OptiSigns account — is refused by name,
 * before anything is billed, and the PO NUMBER still reaches the invoice either way.
 *
 * ── THE UPLOAD HAPPENS FIRST, AND ON ITS OWN ──────────────────────────────────────────────────
 *
 * The file goes to the Document Center before the billing mutation is sent, and the mutation
 * carries only the resulting document id. Two reasons, both learned from the Billing Console's
 * own PO attachment:
 *
 *   - GraphQL carries no binary here (the same wall the quote PDF hit), so the bytes need the
 *     REST route regardless.
 *   - A failed upload must not be able to leave a customer billed. Uploading first means the
 *     operator sees "the upload failed" while nothing has been sent to Stripe, instead of an
 *     invoice that exists with paperwork that does not.
 *
 * The document is filed as `po` — the Document Center's own purchase-order type — under the
 * request's own account, titled with the
 * request number so it is findable later by the thing an operator will actually remember.
 */
import { useState } from 'react';
import { Input, Label } from '@staff-app/ui';
import { intlRequestNo, type IntlRequest } from '@staff-app/shared-types';
import { documentsApi } from '@/features/customers/lib/documents-api';

/** What the parent dialog needs back in order to send its mutation. */
export interface PoValues {
  poNumber: string;
  file: File | null;
}

export function usePoValues(): [PoValues, (v: PoValues) => void] {
  const [values, setValues] = useState<PoValues>({ poNumber: '', file: null });
  return [values, setValues];
}

/**
 * Upload the PO, if there is one, and return the document id for the mutation.
 *
 * Returns undefined when there is nothing to upload — including the accountless case, which the
 * UI has already prevented from producing a file. Throws when the upload itself fails, so the
 * caller can abandon before billing.
 */
export async function uploadPoDocument(
  request: IntlRequest,
  values: PoValues,
): Promise<string | undefined> {
  if (!values.file) return undefined;
  const res = await documentsApi.upload(
    {
      // BOTH, and the server prefers `accountId`. The row's column is a hint that is often empty;
      // `stripeId` is the fact that is always present, and the endpoint resolves it to an account
      // itself rather than making the browser take a second round trip to find out.
      accountId: request.accountId ?? undefined,
      stripeId: request.stripeCustomerId,
      docType: 'po',
      // Named for the REQUEST, because that is what somebody searching the Document Center a
      // month later remembers — not the Stripe customer id and not the original filename.
      title: `Purchase order — ${intlRequestNo(request.id)}`,
      externalNumber: values.poNumber.trim() || undefined,
      counterpartyName: request.company ?? undefined,
    },
    values.file,
  );
  return res.document.id;
}

export function PoAttachment({
  request,
  values,
  onChange,
  disabled,
}: {
  request: IntlRequest;
  values: PoValues;
  onChange: (v: PoValues) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-3" data-testid="intl-po-block">
      <div>
        <Label className="text-xs font-medium text-muted-foreground">
          {'Purchase order number'}
        </Label>
        <Input
          className="mt-1"
          value={values.poNumber}
          disabled={disabled}
          onChange={(e) => onChange({ ...values, poNumber: e.target.value })}
          placeholder="4500123456"
          data-testid="intl-po-number"
        />
        {/* SAYS WHERE IT GOES, because "memo" is not a word a rep can guess the destination of —
            and because a number that will be printed on a document the customer reads deserves a
            second look before it is sent. */}
        <p className="mt-1 text-xs text-muted-foreground">
          {'Printed on the invoice the customer receives. Optional — leave it blank if they have '}
          {'not issued one.'}
        </p>
      </div>

      <div>
        <Label className="text-xs font-medium text-muted-foreground">
          {'Purchase order document'}
        </Label>
        <Input
          className="mt-1"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          disabled={disabled}
          onChange={(e) => onChange({ ...values, file: e.target.files?.[0] ?? null })}
          data-testid="intl-po-file"
        />
        {/* Says where it lands, and does NOT promise it will: whether this customer has a
            Document Center at all is a question only the server can answer, and it answers it
            when the file is sent. Promising less here beats a control that vanishes. */}
        <p className="mt-1 text-xs text-muted-foreground">
          {'Filed in this customer’s Document Center and linked from the invoice in Stripe. '}
          {'Optional.'}
        </p>
      </div>
    </div>
  );
}
